import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import EmailCodePanel from "../components/EmailCodePanel";

const PLATFORMS = ["twitch", "kick", "youtube", "other"];

export default function StreamerOnboarding() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [casinos, setCasinos] = useState([]);
  const [proofs, setProofs] = useState([]);
  const [busy, setBusy] = useState(false);
  // Iter 56 — `loaded` flips true only after GET /streamers/me resolves.
  // Submit is blocked while false, and the payload carries `prefillLoaded`
  // so the backend can distinguish "user removed all" from "form loaded blank".
  const [loaded, setLoaded] = useState(false);
  const [f, setF] = useState({
    displayName: user?.displayName || "", platform: "twitch", channelUrl: "",
    affiliateCasinoIds: [], affiliateCasinoOther: "",
    ageConfirmed: false,
  });

  useEffect(() => { api.get("/casinos").then(({ data }) => setCasinos(data)).catch(() => {}); }, []);

  // Iter 55/56 — pre-fill the form from the streamer's own record so returning
  // from a "needs_more_info" review doesn't require re-typing everything.
  // Reads the onboarding snapshot first (source of truth for the questionnaire),
  // then falls back to top-level streamer fields. `loaded` gates submit so a
  // slow/failed GET can never trigger a blank overwrite on the backend.
  useEffect(() => {
    api.get("/streamers/me").then(({ data }) => {
      const onb = data.onboarding || {};
      const ids = Array.isArray(onb.affiliateCasinoIds) ? onb.affiliateCasinoIds : [];
      const other = onb.affiliateCasinoOther || "";
      setF((prev) => ({
        ...prev,
        displayName: onb.displayName || data.displayName || prev.displayName || "",
        platform: onb.platform || data.platform || prev.platform || "twitch",
        channelUrl: onb.channelUrl || data.channelUrl || "",
        affiliateCasinoIds: ids,
        affiliateCasinoOther: other,
        ageConfirmed: !!(onb.ageConfirmed || data.ageConfirmed),
      }));
      const keys = Array.isArray(onb.proofFileKeys) ? onb.proofFileKeys : [];
      setProofs(keys.map((k) => ({ key: k, filename: String(k).split("/").pop() })));
      setLoaded(true);
    }).catch(() => {
      // Keep loaded=false so submit is blocked; user gets a clear toast on submit.
      toast.error("Couldn't load your saved onboarding. Please refresh before submitting.");
    });
  }, []);

  const removeProof = (key) => setProofs((p) => p.filter((x) => x.key !== key));

  // Server-authoritative email gate — client can never bypass this.
  const checkEmailAndRefresh = async () => {
    try {
      const { data } = await api.get("/auth/email-status");
      if (data.emailVerified) { await refresh(); toast.success(t("streamerOnb.emailUnlocked")); }
      else toast.error(t("streamerOnb.emailStill"));
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const uploadProofs = async (files) => {
    for (const file of files) {
      const form = new FormData(); form.append("file", file); form.append("scope", "streamer-proof");
      try {
        const { data } = await api.post("/streamers/me/proofs", form, { headers: { "Content-Type": "multipart/form-data" } });
        setProofs((p) => [...p, data]);
      } catch (e) { toast.error(apiErrorMessage(e, "Upload failed")); }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    // Iter 56 — hard guard: never submit until we've hydrated existing data.
    // This is a UX safeguard; the backend also merges defensively.
    if (!loaded) {
      toast.error("Still loading your saved onboarding — one moment.");
      return;
    }
    setBusy(true);
    try {
      // Iter 38: streamer onboarding no longer submits country/language/viewersBand
      // (their "About you" block was removed — see PRD).
      await api.post("/streamers/me/onboarding", {
        ...f,
        proofFileKeys: proofs.map((p) => p.key),
        prefillLoaded: true,
      });
      toast.success(t("streamerOnb.submitted"));
      await refresh(); nav("/streamer");
    } catch (err) { toast.error(apiErrorMessage(err, "Submission failed")); } finally { setBusy(false); }
  };

  const toggleCasino = (id) => setF((old) => {
    const next = new Set(old.affiliateCasinoIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    return { ...old, affiliateCasinoIds: [...next] };
  });

  if (user?.emailVerified === false) {
    return (
      <div className="max-w-lg mx-auto px-6 py-24" data-testid="streamer-onboarding-email-locked">
        <EmailCodePanel testIdPrefix="streamer-onb" />
      </div>
    );
  }

  const note = user?.adminNote;
  return (
    <div className="max-w-3xl mx-auto px-6 py-12 lg:py-16">
      <div className="font-mono text-[11px] uppercase tracking-widest text-[#2DD4A7] mb-3">{t("streamerOnb.eyebrow")}</div>
      <h1 className="font-display text-4xl font-black tracking-tighter mb-3" data-testid="streamer-onb-title">{t("streamerOnb.title")}</h1>
      <p className="text-sm text-[#8B97AE] mb-8">{t("streamerOnb.intro")}</p>
      {note && (
        <div className="mb-8 border-l-2 border-[#F5B841] bg-[#F5B841]/[0.06] px-4 py-3" data-testid="streamer-onb-admin-note">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841] mb-1">{t("streamerOnb.needsMore.eyebrow")}</div>
          <div className="text-sm text-[#E8ECF4]">{note}</div>
        </div>
      )}
      <form onSubmit={submit} className="space-y-6" data-testid="streamer-onboarding-form">
        <section className="border border-white/10 p-6 space-y-4">
          <h2 className="font-display font-bold text-lg">1 · {t("streamerOnb.identity")}</h2>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8B97AE]">{t("streamerOnb.displayName")}</Label>
            <Input data-testid="so-displayName" required minLength={2} value={f.displayName} onChange={(e) => setF({ ...f, displayName: e.target.value })} className="rounded-none bg-transparent border-white/20 h-11 mt-2" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#8B97AE]">{t("streamerOnb.platform")}</Label>
              <Select value={f.platform} onValueChange={(v) => setF({ ...f, platform: v })}>
                <SelectTrigger data-testid="so-platform" className="rounded-none bg-transparent border-white/20 h-11 mt-2"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none bg-[#151B28] border-white/20 text-white">
                  {PLATFORMS.map((p) => <SelectItem key={p} value={p} data-testid={`so-platform-${p}`}>{t(`streamerOnb.platforms.${p}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#8B97AE]">{t("streamerOnb.channelUrl")}</Label>
              <Input data-testid="so-channelUrl" required value={f.channelUrl} onChange={(e) => setF({ ...f, channelUrl: e.target.value })} placeholder="https://…" className="rounded-none bg-transparent border-white/20 h-11 mt-2 font-mono text-sm" />
            </div>
          </div>
        </section>

        <section className="border border-white/10 p-6 space-y-3">
          <h2 className="font-display font-bold text-lg">2 · {t("streamerOnb.affiliates")}</h2>
          <p className="text-xs text-[#8B97AE]">{t("streamerOnb.affiliatesHint")}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {casinos.map((c) => (
              <label key={c.id} className="flex items-center gap-2 border border-white/10 px-3 py-2 cursor-pointer hover:border-[#2DD4A7]/50 transition-colors">
                <Checkbox data-testid={`so-casino-${c.id}`} checked={f.affiliateCasinoIds.includes(c.id)} onCheckedChange={() => toggleCasino(c.id)} className="rounded-none border-white/30 data-[state=checked]:bg-[#2DD4A7] data-[state=checked]:text-[#04150F]" />
                <span className="text-sm">{c.name}</span>
              </label>
            ))}
          </div>
          <Input data-testid="so-casino-other" placeholder={t("streamerOnb.otherCasino")} value={f.affiliateCasinoOther} onChange={(e) => setF({ ...f, affiliateCasinoOther: e.target.value })} className="rounded-none bg-transparent border-white/20 h-11 mt-2" />
        </section>

        <section className="border border-white/10 p-6 space-y-3">
          <h2 className="font-display font-bold text-lg">3 · {t("streamerOnb.proof")}</h2>
          <p className="text-xs text-[#F5B841]">{t("streamerOnb.proofHint")}</p>
          <input data-testid="so-proofs-input" type="file" multiple accept="image/jpeg,image/png,application/pdf,video/mp4,video/quicktime,video/webm" onChange={(e) => uploadProofs(Array.from(e.target.files || []))} className="block text-sm text-[#8B97AE] file:mr-3 file:border file:border-white/20 file:bg-transparent file:text-[#E8ECF4] file:px-4 file:py-2 file:font-mono file:text-xs hover:file:bg-white/10" />
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]" data-testid="so-proofs-limits">Accepted: JPG, PNG (up to 10 MB), PDF (up to 25 MB), MP4, MOV, WEBM (up to 50 MB).</p>
          {proofs.length > 0 && (
            <ul className="mt-2 space-y-1" data-testid="so-proofs-list">
              {proofs.map((p, i) => (
                <li key={p.key || i} className="text-[11px] font-mono text-[#8B97AE] flex items-center gap-2" data-testid={`so-proof-row-${i}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2DD4A7] inline-block shrink-0" />
                  <span className="text-[#E8ECF4] truncate">{p.filename || p.key}</span>
                  {p.key && (
                    <button
                      type="button"
                      data-testid={`so-proof-remove-${i}`}
                      onClick={() => removeProof(p.key)}
                      className="ml-auto text-[10px] font-mono uppercase tracking-widest text-[#F26D6D] hover:text-white transition-colors px-1.5 py-0.5 border border-[#F26D6D]/40 hover:border-white"
                      aria-label="Remove proof"
                    >×</button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="border border-white/10 p-6">
          <label className="flex items-start gap-2 cursor-pointer">
            <Checkbox data-testid="so-age" checked={f.ageConfirmed} onCheckedChange={(v) => setF({ ...f, ageConfirmed: !!v })} className="mt-0.5 rounded-none border-white/30 data-[state=checked]:bg-[#2DD4A7] data-[state=checked]:text-[#04150F]" />
            <span className="text-sm text-[#E8ECF4]">{t("streamerOnb.ageConfirm")}</span>
          </label>
        </section>

        <Button data-testid="so-submit" type="submit" disabled={busy || !f.ageConfirmed || !loaded} className="w-full rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] hover:text-[#04150F] h-12 font-semibold transition-colors disabled:opacity-40">
          {busy ? t("common.loading") : (!loaded ? t("common.loading") : t("streamerOnb.submit"))}
        </Button>
      </form>
    </div>
  );
}
